import { css, html } from 'lit';
import { collectBrushTextureUrls } from '../../d3-assets/index.js';
import { SCENES } from '../../d3-assets/render/__fixtures__/scenes.js';
import { SceneViewer } from '../../d3-assets/render/bench/scene-viewer.js';
import { buildTileBrushFromLayers } from '../../d3-assets/render/tile-brush-from-layers.js';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Tile-brush preview. Renders THIS asset's brush across the standard render
 * scenes (straights, corners, T, cross, endings, diagonals, crossings) using the
 * local `drawTileBrush` — the exact renderer this repo publishes as the
 * d3-assets.js bundle — so a preview always matches the shipped composer, with
 * no d3-kit round-trip. Each scene is a pan/zoom SceneViewer (the same widget the
 * dev bench uses). Scroll to zoom, drag to pan, double-click to reset.
 *
 * @property {Array<{layer_name: string, file_url: string}>} layers
 *   The brush's tiles: extracted-part name (our shape vocabulary) + image URL.
 * @property {boolean} [useSeamConnector]
 *   The asset's stored straight-seam-connector policy (`true`/`false`) or
 *   `undefined` (draw the seam connector when present). Read-only here.
 */
class TileBrushDrawPreview extends BaseComponent {
    static properties = {
        layers: { type: Array },
        useSeamConnector: {},
    };

    static styles = css`
        :host { display: block; margin-top: 1rem; }
        h6 {
            font-size: 0.85rem; font-weight: 600; text-transform: uppercase;
            letter-spacing: 0.04em; color: #6c757d; margin: 0 0 0.5rem;
        }
        .hint { font-size: 0.72rem; color: #6c757d; margin: 0 0 0.6rem; }
        #grid { display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-start; }
        figure {
            margin: 0; background: #fff; border: 1px solid #dee2e6;
            border-radius: 0.375rem; padding: 8px;
        }
        figcaption {
            font-size: 0.72rem; font-weight: 600; color: #57606a; margin: 0 0 6px;
            display: flex; align-items: center; gap: 6px;
        }
        figcaption .tag {
            display: inline-flex; align-items: center; justify-content: center;
            width: 18px; height: 18px; border-radius: 4px; background: #24292f;
            color: #fff; font-size: 11px; font-weight: 700;
        }
        .viewer { display: flex; flex-direction: column; gap: 6px; }
        canvas { display: block; border: 1px solid #eaecef; border-radius: 4px; touch-action: none; cursor: grab; }
        .view-controls { display: flex; align-items: center; gap: 6px; }
        .view-controls button {
            font-size: 12px; line-height: 1; padding: 3px 8px;
            border: 1px solid #d0d7de; border-radius: 5px;
            background: #fff; color: #57606a; cursor: pointer;
        }
        .view-controls button:hover { background: #f3f4f6; }
        .view-controls .zoom-readout {
            font-size: 11px; color: #6c757d; min-width: 38px;
            text-align: center; font-variant-numeric: tabular-nums;
        }
    `;

    constructor() {
        super();
        this.layers = [];
        this.useSeamConnector = undefined;
        this._sig = '';
    }

    updated() {
        // Rebuild only when the tile set or the seam policy actually changes.
        const sig =
            (this.layers || [])
                .map((l) => `${l.layer_name}=${l.file_url}`)
                .sort()
                .join('|') +
            '#' +
            String(this.useSeamConnector);
        if (sig !== this._sig) {
            this._sig = sig;
            void this._rebuild();
        }
    }

    /** A tile-brush manifest whose tiles are this asset's extracted parts. */
    _buildBrush() {
        return buildTileBrushFromLayers(this.layers, {
            label: 'This brush',
            useStraightSeamConnector: this.useSeamConnector,
        });
    }

    async _rebuild() {
        const grid = this.renderRoot?.querySelector('#grid');
        if (!grid || (this.layers || []).length === 0) return;

        const brush = this._buildBrush();
        const textures = await this._loadTextures(brush);

        grid.replaceChildren();
        for (const scene of SCENES) {
            const viewer = new SceneViewer({ scene, textures, getBrush: () => brush });
            const fig = document.createElement('figure');
            const cap = document.createElement('figcaption');
            const tag = document.createElement('span');
            tag.className = 'tag';
            tag.textContent = scene.label;
            cap.append(tag, document.createTextNode(scene.title));
            fig.append(cap, viewer.element);
            grid.append(fig);
        }
    }

    /** Preload every tile URL the brush references into a decoded-image Map. */
    async _loadTextures(brush) {
        const urls = [...new Set(collectBrushTextureUrls(brush))];
        const entries = await Promise.all(
            urls.map(async (url) => {
                const img = new Image();
                img.src = url;
                await img.decode().catch(() => {});
                return /** @type {[string, HTMLImageElement]} */ ([url, img]);
            }),
        );
        return new Map(entries);
    }

    render() {
        if ((this.layers || []).length === 0) {
            return html`<p class="text-muted small">No extracted tiles yet — the preview appears once this brush's parts are available.</p>`;
        }
        return html`
            <h6>Preview</h6>
            <p class="hint">This brush composed across the standard shapes — the same renderer the game uses, reflecting this brush's saved settings. Scroll to zoom, drag to pan, double-click to reset.</p>
            <div id="grid"></div>
        `;
    }
}

customElements.define('tile-brush-draw-preview', TileBrushDrawPreview);
